import { Auth } from '@easylife365/react/auth';
import { TaskDetailProps } from './types';
import { Formik } from 'formik';
import {
  BulmaType,
  Button,
  ButtonProps,
  Checkbox,
  FontWeight,
  formatDate,
  Input,
  Label,
  Loader,
  Message,
  PropertyValue,
  Select,
  Tag,
  TaskPanel,
  Textarea,
  TryAgain,
  GraphPicker,
  UserType,
} from '@easylife365/react';
import { faExternalLink, faSave } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { ChangeEvent, useMemo } from 'react';
import { useHandleHttpError, useShowEntraIdLinks } from '../../../lib/hooks';
import { appQueries, useUpsertTask } from '../../../api/app.api';
import { isRefusal } from '../../../api/http';
import { graphQueries } from '../../../api/graph.api';
import { CredentialTaskDto, CredentialType, IdentityTaskStatus, isTaskFor, ResourceType } from '../../../api/app.types';
import { getTaskStateOptions } from '../../../lib/model';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { sameId } from '../../../lib/ids';
import AssigneePicker from './AssigneePicker';

// Mirrors UpsertTaskParameters.MaxCommentLength.
const MAX_COMMENT_LENGTH = 2000;

const TaskDetailPanel: React.FC<TaskDetailProps> = ({ task, close }) => {
  const { t } = useTranslation('taskDetailPanel');
  const { t: tCommon } = useTranslation('common');
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the option text follows the language, which tCommon tracks
  const taskStateOptions = useMemo(() => getTaskStateOptions(), [tCommon]);
  const showEntraIdLinks = useShowEntraIdLinks();
  const handleHttpError = useHandleHttpError();

  // This is a fallback value needed because we use 'task' outside of the panel, even when there is none.
  // We need a better way to handle values for disclosable when they have Formik around, etc.
  const definedTask: CredentialTaskDto = task ?? {
    keyId: '',
    appId: '',
    resourceType: ResourceType.AppRegistration,
    isSaml: false,
    type: CredentialType.Certificate,
  };

  const {
    isPending: isGetPermissionPending,
    isError: isGetPermissionError,
    data: permissions,
    refetch: refetchGetPermissions,
  } = useQuery({
    ...appQueries.getPermissions(definedTask.resourceType, definedTask.appId),
    enabled: !!task,
  });

  const {
    isError: isUpsertError,
    error: upsertError,
    mutateAsync: upsertTask,
    reset: resetUpsertTask,
  } = useUpsertTask({
    onSuccess: close,
  });

  const { data: storedTask } = useQuery({
    ...appQueries.getTask(definedTask.keyId),
    enabled: !!task,
  });
  // A task stored against another application is not this credential's task.
  const taskDetails =
    storedTask && isTaskFor(storedTask, definedTask.keyId, definedTask.appId) ? storedTask : undefined;

  const { data: app } = useQuery({
    ...graphQueries.getResource(definedTask.resourceType, definedTask.appId),
    enabled: !!task,
  });

  const { data: adminInfo } = useQuery(graphQueries.isAppMgmtAdmin);

  // Whether the tenant limits who a task can be assigned to, and who that is. Where the list cannot be read, the picker
  // is the one it was before and the server decides.
  const {
    data: assignees,
    isError: assigneesFailed,
    isLoading: assigneesLoading,
  } = useQuery({
    ...appQueries.getTaskAssignees(definedTask.resourceType, definedTask.appId),
    enabled: !!task && !!permissions?.editCredentials,
  });

  const isOwner = app?.owners.some((owner) => sameId(owner.id, Auth.getInstance().getUserAccount()?.id)) ?? false;
  const isOwnerOrAdmin = isOwner || (adminInfo?.isAdmin ?? false);

  if (isGetPermissionError)
    return (
      <>
        <TryAgain />
        <Button onClick={() => refetchGetPermissions()}>{tCommon('actions.retry')}</Button>
      </>
    );

  // A refusal is reported by the submit handler and leaves the form in place, so it never reaches the retry screen.
  if (isUpsertError && !isRefusal(upsertError))
    return (
      <>
        <TryAgain />
        <Button onClick={resetUpsertTask}>{tCommon('actions.retry')}</Button>
      </>
    );

  const userId = Auth.getInstance().getUserAccount()?.id;
  const onTaskSubmit = async (values: Readonly<CredentialTaskDto>) => {
    try {
      await upsertTask({
        id: values.keyId,
        request: {
          appObjectId: values.appId,
          credentialType: values.type,
          status: values.status ?? IdentityTaskStatus.New,
          comment: isOwnerOrAdmin ? values.comment : null,
          ownerId: values.assignMyself ? userId : values.ownerId,
        },
      });
    } catch (error) {
      // A refusal the server explains (an owner who is a guest, a task that belongs to another application) is shown
      // over the form, which keeps the edits. Any other failure leaves the retry screen as before.
      if (!isRefusal(error)) throw error;

      handleHttpError(error);
      resetUpsertTask();
    }
  };

  const canEdit = !isGetPermissionPending && permissions.editCredentials;
  const canEditStatus = canEdit && (isOwnerOrAdmin || sameId(taskDetails?.ownerId, userId));

  return (
    <Formik
      initialValues={{ ...definedTask, ...taskDetails }}
      onSubmit={onTaskSubmit}
      enableReinitialize
      validateOnMount={true}
    >
      {({ values, dirty, isSubmitting, isValid, handleSubmit, resetForm, setFieldValue }) => {
        const userId = Auth.getInstance().getUserAccount()?.id;
        const onAssignMyselfClick = (e: ChangeEvent<HTMLInputElement>) => {
          if (e.target.checked && !sameId(values.ownerId, userId)) {
            setFieldValue('ownerId', undefined, true);
          }
        };

        const onCancel = () => {
          resetForm();
          close();
        };

        const ctaButtons: ButtonProps[] = [
          ...(showEntraIdLinks
            ? [
                {
                  to: getTargetLink(values),
                  target: '_blank',
                  text: 'Entra ID',
                  icon: faExternalLink,
                },
              ]
            : []),
          ...(canEdit
            ? [
                {
                  bulmaType: BulmaType.Primary,
                  disabled: !dirty || isSubmitting || !isValid,
                  text: tCommon('actions.save'),
                  icon: faSave,
                  loading: isSubmitting,
                  type: 'submit',
                },
              ]
            : []),
        ] as ButtonProps[];

        const canEditComment = !isGetPermissionPending && isOwnerOrAdmin;

        return (
          <form onSubmit={handleSubmit}>
            <TaskPanel
              ctaButtons={ctaButtons}
              title={values.appDisplayName ?? ''}
              isVisible={!!task}
              onCancel={onCancel}
            >
              {isGetPermissionPending ? (
                <Loader />
              ) : (
                <>
                  {!canEdit && <Message title={t('noPermission')} bulmaType={BulmaType.Warning} />}
                  {assigneesFailed && <Message title={t('assigneesFailed')} bulmaType={BulmaType.Warning} />}
                  {assigneesLoading ? (
                    <Loader />
                  ) : assignees?.restricted ? (
                    <AssigneePicker
                      assignees={assignees.assignees}
                      disabled={!canEdit || !!values.assignMyself}
                      formLabel={values.ownerId ? t('assignedTo') : t('assignTask')}
                    />
                  ) : (
                    <GraphPicker
                      disabled={!canEdit || !!values.assignMyself}
                      formLabel={values.ownerId ? t('assignedTo') : t('assignTask')}
                      name="ownerId"
                      targetPropertyValue={PropertyValue.Id}
                      // The owner is notified in place of the application owners, and the server refuses a guest.
                      userType={UserType.Member}
                    />
                  )}
                  {!sameId(values.ownerId, userId) &&
                    (!assignees?.restricted || assignees.assignees.some((assignee) => sameId(assignee.id, userId))) && (
                      <Checkbox
                        label={t('assignMyself')}
                        disabled={!canEdit}
                        onChange={onAssignMyselfClick}
                        name="assignMyself"
                        helpMessage={t('assignMyselfHelp')}
                      />
                    )}
                  <Select
                    formLabel={tCommon('fields.taskStatus')}
                    options={taskStateOptions}
                    disabled={!canEditStatus}
                    name="status"
                  />
                  {(isOwnerOrAdmin || sameId(values.ownerId, userId)) && (
                    <Textarea
                      formLabel={t('comment')}
                      disabled={!canEditComment}
                      name="comment"
                      maxLength={MAX_COMMENT_LENGTH}
                    />
                  )}

                  {values.thumbprint && (
                    <Input
                      formLabel={tCommon('fields.thumbprint')}
                      name="thumbprint"
                      disabled={true}
                      copyButton={true}
                    />
                  )}

                  {values.keyId && (
                    <Input formLabel={getIdLabel(values.type, t)} disabled={true} name="keyId" copyButton={true} />
                  )}
                  <div className="labeled-data">
                    <Label label={t('expiringOn')} fontWeight={FontWeight.SemiBold} />
                    <Tag
                      label={formatDate(values?.expires) ?? ''}
                      title={formatDate(values?.expires, {
                        useRelativeTime: false,
                        formatOptions: {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: 'numeric',
                          minute: 'numeric',
                        },
                      })}
                      bulmaType={BulmaType.Danger}
                    />
                  </div>
                </>
              )}
            </TaskPanel>
          </form>
        );
      }}
    </Formik>
  );
};

function getTargetLink(values: CredentialTaskDto) {
  switch (values.resourceType) {
    case ResourceType.AppRegistration:
      return `https://entra.microsoft.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/Credentials/appId/${values.appObjectId}/isMSAApp~/false`;

    case ResourceType.EnterpriseApplication:
      return `https://entra.microsoft.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Overview/objectId/${values.appId}/appId/${values.appObjectId}/preferredSingleSignOnMode~/null/servicePrincipalType/Application/fromNav/`;
  }
}

function getIdLabel(type: CredentialType, t: TFunction<'taskDetailPanel'>) {
  switch (type) {
    case CredentialType.Certificate:
      return t('certificateId');

    case CredentialType.Secret:
      return t('secretId');
  }
}

export default TaskDetailPanel;
